import { loadable } from '@fiverr-private/perseus';
import { appTranslate } from '../../../i18n';
import { PackageDetails } from '../../../components/PackageDetails/types';
import { SubscriptionTier } from '../../../../type';
import { Microsoft365IconProps } from '../../../components/Icons/microsoft_365';
import * as Icons from '../../../components/Icons/microsoft_365';

type IconKeys = keyof typeof Icons;

const lazyLoadIcon = (iconName: IconKeys) =>
    loadable<Microsoft365IconProps>(
        () => import(`../../../components/Icons/microsoft_365/`).then((module) => module[iconName]),
        { ssr: false }
    );

const products = {
    word: { name: 'Word', icon: lazyLoadIcon('WordIcon') },
    excel: { name: 'Excel', icon: lazyLoadIcon('ExcelIcon') },
    powerPoint: { name: 'PowerPoint', icon: lazyLoadIcon('PowerPointIcon') },
    outlook: { name: 'Outlook', icon: lazyLoadIcon('OutlookIcon') },
    teams: { name: 'Teams', icon: lazyLoadIcon('TeamsIcon') },
    oneDrive: { name: 'OneDrive', icon: lazyLoadIcon('OneDriveIcon') },
    sharePoint: { name: 'SharePoint', icon: lazyLoadIcon('SharePointIcon') },
    exchange: { name: 'Exchange', icon: lazyLoadIcon('ExchangeIcon') },
    clipChamp: { name: 'Clipchamp', icon: lazyLoadIcon('ClipChampIcon') },
    microsoftLoop: { name: 'Microsoft Loop', icon: lazyLoadIcon('MicrosoftLoopIcon') },
    microsoftEntraId: { name: 'Microsoft Entra ID', icon: lazyLoadIcon('MicrosoftEntraIdIcon') },
    intune: { name: 'Intune', icon: lazyLoadIcon('IntuneIcon') },
    microsoftDefender: { name: 'Microsoft Defender', icon: lazyLoadIcon('MicrosoftDefenderIcon') },
    microsoftPurview: { name: 'Microsoft Purview', icon: lazyLoadIcon('MicrosoftPurviewIcon') },
};

export const getPackageDetails = (): PackageDetails[] => [
    {
        id: SubscriptionTier.Basic,
        shortInfo: [
            appTranslate('package_details.microsoft.card_1.short_info.short_info_option_1.text'),
            appTranslate('package_details.microsoft.card_1.short_info.short_info_option_2.text'),
        ],
        advantages: {
            title: appTranslate<string>('package_details.microsoft.card_1.advantages.title'),
            list: [
                appTranslate('package_details.microsoft.card_1.advantages.list_options.list_option_1.text'),
                appTranslate('package_details.microsoft.card_1.advantages.list_options.list_option_2.text'),
                appTranslate('package_details.microsoft.card_1.advantages.list_options.list_option_3.text'),
                appTranslate('package_details.microsoft.card_1.advantages.list_options.list_option_4.text'),
                appTranslate('package_details.microsoft.card_1.advantages.list_options.list_option_5.text'),
                appTranslate('package_details.microsoft.card_1.advantages.list_options.list_option_6.text'),
                appTranslate('package_details.microsoft.card_1.advantages.list_options.list_option_7.text'),
                appTranslate('package_details.microsoft.card_1.advantages.list_options.list_option_8.text'),
            ],
        },
        productGroups: [
            {
                title: appTranslate('package_details.microsoft.card_1.product_groups.group_title_1.text'),
                products: [products.teams, products.oneDrive, products.sharePoint, { ...products.exchange }],
            },
            {
                title: appTranslate('package_details.microsoft.card_1.product_groups.group_title_2.text'),
                products: [products.word, products.excel, products.powerPoint, products.outlook],
            },
        ],
        vendor: {
            title: appTranslate('common.vendors.microsoft_365'),
            label: appTranslate('common.subscription_tiers.business_basic'),
        },
    },
    {
        id: SubscriptionTier.Standard,
        shortInfo: [
            appTranslate('package_details.microsoft.card_2.short_info.short_info_option_1.text'),
            appTranslate('package_details.microsoft.card_2.short_info.short_info_option_2.text'),
        ],
        advantages: {
            title: appTranslate<string>('package_details.microsoft.card_2.advantages.title'),
            list: [
                appTranslate('package_details.microsoft.card_2.advantages.list_options.list_option_1.text'),
                appTranslate('package_details.microsoft.card_2.advantages.list_options.list_option_2.text'),
                appTranslate('package_details.microsoft.card_2.advantages.list_options.list_option_3.text'),
                appTranslate('package_details.microsoft.card_2.advantages.list_options.list_option_4.text'),
            ],
        },
        productGroups: [
            {
                title: appTranslate('package_details.microsoft.card_2.product_groups.group_title_1.text'),
                products: [
                    products.word,
                    products.excel,
                    products.powerPoint,
                    products.outlook,
                    products.teams,
                    products.oneDrive,
                    products.sharePoint,
                    { ...products.exchange },
                    { ...products.clipChamp },
                    { ...products.microsoftLoop },
                ],
            },
        ],
        vendor: {
            title: appTranslate('common.vendors.microsoft_365'),
            label: appTranslate('common.subscription_tiers.business_standard'),
        },
    },
    {
        id: SubscriptionTier.Premium,
        shortInfo: [
            appTranslate('package_details.microsoft.card_3.short_info.short_info_option_1.text'),
            appTranslate('package_details.microsoft.card_3.short_info.short_info_option_2.text'),
        ],
        advantages: {
            title: appTranslate<string>('package_details.microsoft.card_3.advantages.title'),
            list: [
                appTranslate('package_details.microsoft.card_3.advantages.list_options.list_option_1.text'),
                appTranslate('package_details.microsoft.card_3.advantages.list_options.list_option_2.text'),
                appTranslate('package_details.microsoft.card_3.advantages.list_options.list_option_3.text'),
                appTranslate('package_details.microsoft.card_3.advantages.list_options.list_option_4.text'),
            ],
        },
        productGroups: [
            {
                title: appTranslate('package_details.microsoft.card_3.product_groups.group_title_1.text'),
                products: [
                    products.word,
                    products.excel,
                    products.powerPoint,
                    products.outlook,
                    products.teams,
                    products.oneDrive,
                    products.sharePoint,
                    { ...products.exchange },
                    { ...products.clipChamp },
                    { ...products.microsoftLoop },
                    { ...products.microsoftEntraId },
                    { ...products.intune },
                    { ...products.microsoftDefender },
                    { ...products.microsoftPurview },
                ],
            },
        ],
        vendor: {
            title: appTranslate('common.vendors.microsoft_365'),
            label: appTranslate('common.subscription_tiers.business_premium'),
        },
    },
    {
        id: SubscriptionTier.Apps,
        shortInfo: [
            appTranslate('package_details.microsoft.card_4.short_info.short_info_option_1.text'),
            appTranslate('package_details.microsoft.card_4.short_info.short_info_option_2.text'),
        ],
        advantages: {
            list: [
                appTranslate('package_details.microsoft.card_4.advantages.list_options.list_option_1.text'),
                appTranslate('package_details.microsoft.card_4.advantages.list_options.list_option_2.text'),
                appTranslate('package_details.microsoft.card_4.advantages.list_options.list_option_3.text'),
            ],
        },
        productGroups: [
            {
                title: appTranslate('package_details.microsoft.card_4.product_groups.group_title_1.text'),
                products: [products.word, products.excel, products.powerPoint, products.outlook, products.oneDrive],
            },
        ],
        vendor: {
            title: appTranslate('common.vendors.microsoft_365'),
            label: appTranslate('common.subscription_tiers.apps_for_business'),
        },
    },
];
